/**
 * PWA bootstrap helpers.
 *
 * Two responsibilities:
 *   - registerServiceWorker(): called from index.tsx on every page load
 *     so the PWA install prompt becomes available + offline-shell caching
 *     kicks in. Safe to call even if push.ts later re-registers — the
 *     browser dedupes by script URL.
 *   - installPromptStore(): captures the browser's `beforeinstallprompt`
 *     event so the rest of the app can offer an "Install" button at a
 *     time of its choosing (Chrome/Edge only fire the event once and
 *     stash it on the window; the UX is much better when we trigger it
 *     from a real click later).
 */

let deferredPromptEvent: any = null;
const listeners: Array<(canInstall: boolean) => void> = [];

export function registerServiceWorker(): void {
  // Don't register in dev — the CRA dev server doesn't serve /sw.js
  // reliably and stale SWs break HMR. In prod the script is at /sw.js.
  if (process.env.NODE_ENV !== "production") return;
  if (!("serviceWorker" in navigator)) return;

  // Was this page already controlled by a SW at load time? If NOT, the first
  // controllerchange is just the initial clients.claim() on a fresh load and
  // must not trigger a reload. If it WAS controlled, a later controllerchange
  // means a NEW build's SW replaced the old one → reload to pick it up.
  const wasControlledAtLoad = !!navigator.serviceWorker.controller;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        // Proactively check for a newer SW on each load so a deploy is picked
        // up without waiting for the browser's periodic update check.
        reg.update().catch(() => {});
      })
      .catch((err) => {
        // Non-fatal — the SPA still works without the SW. Log so devs
        // notice if registration regresses in CI.
        // eslint-disable-next-line no-console
        console.warn("Service worker registration failed:", err);
      });
  });

  // When a new service worker takes control (a fresh deploy activated, via
  // skipWaiting + clients.claim in sw.js), reload ONCE so the tab picks up the
  // new build immediately instead of running stale JS until the user happens
  // to hard-refresh. The guard prevents a reload loop.
  let reloadedForNewSW = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloadedForNewSW || !wasControlledAtLoad) return;
    reloadedForNewSW = true;
    window.location.reload();
  });

  window.addEventListener("beforeinstallprompt", (e: any) => {
    e.preventDefault();
    deferredPromptEvent = e;
    listeners.forEach((l) => l(true));
  });

  window.addEventListener("appinstalled", () => {
    deferredPromptEvent = null;
    listeners.forEach((l) => l(false));
  });
}

export function canInstall(): boolean {
  return !!deferredPromptEvent;
}

export function subscribeInstallable(cb: (canInstall: boolean) => void): () => void {
  listeners.push(cb);
  cb(canInstall());
  return () => {
    const i = listeners.indexOf(cb);
    if (i >= 0) listeners.splice(i, 1);
  };
}

export async function triggerInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferredPromptEvent) return "unavailable";
  try {
    deferredPromptEvent.prompt();
    const choice = await deferredPromptEvent.userChoice;
    deferredPromptEvent = null;
    listeners.forEach((l) => l(false));
    return choice?.outcome === "accepted" ? "accepted" : "dismissed";
  } catch {
    return "dismissed";
  }
}

export function isStandaloneDisplay(): boolean {
  // iOS Safari only exposes navigator.standalone; everyone else uses
  // the media query. Treat either as "running as installed PWA".
  // @ts-expect-error — non-standard, iOS only.
  if (window.navigator.standalone) return true;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  return false;
}
